import React, { useEffect, useState } from 'react';
import { useAppStore } from './hooks/useAppStore';
import { MODULES, getAllQuestions } from './data/questionLoader';
import { AppShellRedesigned } from './components/layout/AppShellRedesigned';
import { HeroSection } from './components/dashboard/HeroSection';
import { ProgressSummaryCard } from './components/dashboard/ProgressSummaryCard';
import { QuickActions } from './components/dashboard/QuickActions';
import { ModuleCardNew } from './components/dashboard/ModuleCardNew';
import { ContinueLearning } from './components/dashboard/ContinueLearning';
import { FocusAreas } from './components/dashboard/FocusAreas';
import { RecentActivity } from './components/dashboard/RecentActivity';
import { ModulePanelRedesigned } from './components/module/ModulePanelRedesigned';
import { PracticeSession } from './components/practice/PracticeSession';
import { LevelUpModal } from './components/LevelUpModal';
import { CitoSimulator } from './components/bonus/CitoSimulator';
import { CheatSheetDrawer } from './components/practice/CheatSheetDrawer';
import { SettingsPage } from './components/settings/SettingsPage';
import { DeveloperPage } from './components/dev/DeveloperPage';
import { PlacementTest } from './components/practice/PlacementTest';
import { ProgressPage } from './components/progress/ProgressPage';
import { ModuleId, Question, ModuleData } from './types';
import { SESSION_CONFIG } from './constants';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from './components/ui/Button';
import { Card } from './components/ui/Card';
// Safe question loading with error handling
let QUESTIONS: Question[] = [];
try {
  QUESTIONS = getAllQuestions();
  if (QUESTIONS.length === 0) {
    console.warn('⚠️ No questions loaded - check question data files');
  }
} catch (error) {
  console.error('❌ Failed to load questions:', error);
  QUESTIONS = [];
}
type AppView = 'dashboard' | 'practice' | 'citoSimulator' | 'placement';
type NavView = 'dashboard' | 'modules' | 'progress' | 'settings' | 'developer';
export function App() {
  const {
    state,
    selectedGroup,
    setSelectedGroup,
    startingPeriod,
    manualPeriod,
    setStartingPeriod,
    recordAnswer,
    markCheatSheetViewed,
    clearPendingLevelUp,
    devJumpToLevel,
    devResetProgress
  } = useAppStore();
  const [currentView, setCurrentView] = useState<AppView>('dashboard');
  const [currentNavView, setCurrentNavView] = useState<NavView>('dashboard');
  const [activeModuleId, setActiveModuleId] = useState<ModuleId | null>(null);
  const [isFocusSession, setIsFocusSession] = useState(false);
  const [focusQuestions, setFocusQuestions] = useState<Question[]>([]);
  // Module Panel State
  const [selectedModule, setSelectedModule] = useState<ModuleData | null>(null);
  const [cheatSheetModuleId, setCheatSheetModuleId] = useState<ModuleId | null>(null);
  // Placement test state (for dashboard-triggered placement)
  const [placementModuleId, setPlacementModuleId] = useState<ModuleId | null>(null);
  // Calculate stats for the new components - use progressive mastery threshold
  const masteredCount = Object.values(state.progress).filter(p => p.masteryPercentage >= 95).length;
  const totalModules = MODULES.length;
  // Calculate total questions answered across all modules
  const totalQuestionsAnswered = Object.values(state.progress).reduce((sum, p) => sum + p.questionsAnswered.length, 0);
  // Build recommendation for HeroSection
  const getRecommendation = () => {
    const activeModule = Object.entries(state.progress).filter(([_, progress]) => {
      const skills = Object.values(progress.subSkills || {});
      return skills.length > 0 && skills.some(s => s.masteryScore < 80);
    }).sort((a, b) => {
      const lastA = Math.max(...Object.values(a[1].subSkills || {}).map(s => s.lastPracticed), 0);
      const lastB = Math.max(...Object.values(b[1].subSkills || {}).map(s => s.lastPracticed), 0);
      return lastB - lastA;
    })[0];
    if (activeModule) {
      const moduleId = activeModule[0] as ModuleId;
      const moduleData = MODULES.find(m => m.id === moduleId);
      return {
        type: 'continue' as const,
        title: 'Ga verder waar je was',
        description: `Je bent goed bezig met ${moduleData?.title}. Blijf oefenen om je plantjes te laten groeien!`,
        action: 'Ga verder',
        onClick: () => setSelectedModule(MODULES.find(m => m.id === moduleId) || null)
      };
    } else {
      return {
        type: 'start' as const,
        title: 'Klaar om te beginnen?',
        description: 'Kies een module en begin met oefenen. Elke dag een beetje beter!',
        action: 'Kies een module',
        onClick: () => setCurrentNavView('modules')
      };
    }
  };
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (placementModuleId) setPlacementModuleId(null);else if (cheatSheetModuleId) setCheatSheetModuleId(null);else if (selectedModule) setSelectedModule(null);
      }
      // Dev shortcut now navigates to the page instead of opening modal
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setCurrentNavView('developer');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cheatSheetModuleId, selectedModule, placementModuleId]);
  // Handlers
  const handleStartPractice = (moduleId: ModuleId) => {
    markCheatSheetViewed(moduleId);
    setActiveModuleId(moduleId);
    setCurrentView('practice');
    setIsFocusSession(false);
    setSelectedModule(null);
  };
  const handleStartFocusSession = () => {
    const shuffled = [...QUESTIONS].sort(() => Math.random() - 0.5);
    const selectedQuestions = shuffled.slice(0, SESSION_CONFIG.QUESTIONS_PER_SESSION);
    setFocusQuestions(selectedQuestions);
    setActiveModuleId(null);
    setCurrentView('practice');
    setIsFocusSession(true);
  };
  const handleStartPlacement = () => {
    // Pick a module that hasn't had a placement test yet, or the first module
    const unplacedModule = MODULES.find(m => {
      const key = `${m.id}_${selectedGroup}`;
      return !state.placementResults?.[key];
    });
    if (unplacedModule) {
      setPlacementModuleId(unplacedModule.id);
    } else {
      // All modules have placement — pick the first one to redo
      setPlacementModuleId(MODULES[0].id);
    }
  };
  const handlePracticeComplete = () => {
    setActiveModuleId(null);
    setCurrentView('dashboard');
    setIsFocusSession(false);
    setFocusQuestions([]);
  };
  // Error State
  if (QUESTIONS.length === 0) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-id="element-1">
        <Card className="max-w-md w-full p-8 text-center" data-id="element-2">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4" data-id="element-3">
            <AlertTriangle className="w-8 h-8 text-amber-600" data-id="element-4" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-5">
            Vragen laden mislukt
          </h2>
          <Button onClick={() => window.location.reload()} variant="primary" size="lg" data-id="element-6">
            <RotateCcw className="w-4 h-4 mr-2" data-id="element-7" /> Pagina herladen
          </Button>
        </Card>
      </div>;
  }
  // Full Screen Views (No Shell)
  if (currentView === 'practice') {
    return <>
        <PracticeSession moduleId={activeModuleId} questions={isFocusSession ? focusQuestions : []} onComplete={handlePracticeComplete} onExit={() => {
        setActiveModuleId(null);
        setCurrentView('dashboard');
        setIsFocusSession(false);
        setFocusQuestions([]);
      }} onAnswer={(qId, correct, choice) => {
        const question = QUESTIONS.find(q => q.id === qId);
        if (question) {
          recordAnswer(question.module, qId, correct, choice);
        }
      }} cheatSheetViewed={activeModuleId ? state.progress[activeModuleId]?.cheatSheetViewed ?? false : true} onCheatSheetView={() => activeModuleId && markCheatSheetViewed(activeModuleId)} isCrossModule={isFocusSession} data-id="element-8" />
        {state.pendingLevelUp && <LevelUpModal isOpen={true} level={state.pendingLevelUp} xp={state.stats.xp} onClose={clearPendingLevelUp} data-id="element-9" />}
      </>;
  }
  if (currentView === 'citoSimulator') {
    return <CitoSimulator onExit={() => setCurrentView('dashboard')} onAnswer={() => {}} data-id="element-10" />;
  }
  // Main App with Redesigned Shell
  return <AppShellRedesigned stats={state.stats} currentView={currentNavView} onNavigate={view => setCurrentNavView(view as NavView)} selectedGroup={selectedGroup} onSelectGroup={setSelectedGroup} data-id="element-11">
      {/* Dashboard View */}
      {currentNavView === 'dashboard' && <div className="space-y-6" data-id="element-12">
          {/* 1. Hero Section - Compact & Welcoming */}
          <HeroSection userName={state.stats.name} recommendation={getRecommendation()} data-id="element-13" />

          {/* 2. Primary Action: Continue Learning - Promoted to top */}
          <ContinueLearning modules={MODULES} progress={state.progress} onStartPractice={moduleId => {
        setSelectedModule(MODULES.find(m => m.id === moduleId) || null);
      }} onNavigateToModules={() => setCurrentNavView('modules')} selectedGroup={selectedGroup} data-id="element-14" />

          {/* 3. Stats Summary */}
          <ProgressSummaryCard stats={state.stats} masteredCount={masteredCount} totalModules={totalModules} totalQuestionsAnswered={totalQuestionsAnswered} data-id="element-15" />

          {/* 4. Quick Actions */}
          <QuickActions onStartDaily={() => {
        const randomModule = MODULES[Math.floor(Math.random() * MODULES.length)];
        setSelectedModule(randomModule);
      }} onStartReview={handleStartFocusSession} onStartSimulator={() => setCurrentView('citoSimulator')} onStartPlacement={handleStartPlacement} data-id="element-16" />

          {/* 5. Personalized Sections Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-17">
            <div className="lg:col-span-2" data-id="element-18">
              <FocusAreas state={state} modules={MODULES} selectedGroup={selectedGroup} onStartPractice={moduleId => {
            setSelectedModule(MODULES.find(m => m.id === moduleId) || null);
          }} data-id="element-19" />
            </div>

            <div className="lg:col-span-1" data-id="element-20">
              <RecentActivity state={state} modules={MODULES} data-id="element-21" />
            </div>
          </div>
        </div>}

      {/* Modules View */}
      {currentNavView === 'modules' && <>
          <div className="mb-6" data-id="element-22">
            <h1 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-23">
              Alle Vakken
            </h1>
            <p className="text-slate-600" data-id="element-24">Kies een vak om te oefenen</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-25">
            {MODULES.map(module => <ModuleCardNew key={module.id} module={module} progress={state.progress[module.id]} onClick={() => setSelectedModule(module)} selectedGroup={selectedGroup} data-id="element-26" />)}
          </div>
        </>}

      {/* Progress View */}
      {currentNavView === 'progress' && <ProgressPage state={state} selectedGroup={selectedGroup} data-id="element-27" />}

      {/* Settings View */}
      {currentNavView === 'settings' && <SettingsPage state={state} selectedGroup={selectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} onSetStartingPeriod={setStartingPeriod} data-id="element-28" />}

      {/* Developer View */}
      {currentNavView === 'developer' && <DeveloperPage state={state} questions={QUESTIONS} selectedGroup={selectedGroup} onJumpToLevel={devJumpToLevel} onResetProgress={devResetProgress} data-id="element-29" />}

      {/* Modals & Panels */}
      <ModulePanelRedesigned isOpen={!!selectedModule} onClose={() => setSelectedModule(null)} module={selectedModule} progress={selectedModule ? state.progress[selectedModule.id] : null} onStartPractice={handleStartPractice} onViewExplanation={moduleId => {
      setCheatSheetModuleId(moduleId as ModuleId);
      setSelectedModule(null);
    }} selectedGroup={selectedGroup} data-id="element-30" />

      <CheatSheetDrawer isOpen={!!cheatSheetModuleId} onClose={() => setCheatSheetModuleId(null)} moduleId={cheatSheetModuleId!} data-id="element-31" />

      {/* Dashboard Placement Test Modal */}
      {placementModuleId && <>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" onClick={() => setPlacementModuleId(null)} data-id="element-32" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-33">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" data-id="element-34">
              <PlacementTest moduleId={placementModuleId} onComplete={() => setPlacementModuleId(null)} onCancel={() => setPlacementModuleId(null)} data-id="element-35" />
            </div>
          </div>
        </>}

      {state.pendingLevelUp && <LevelUpModal isOpen={true} level={state.pendingLevelUp} xp={state.stats.xp} onClose={clearPendingLevelUp} data-id="element-36" />}
    </AppShellRedesigned>;
}